// pages/tasks/OwnTicketStatusPicker.jsx
//
// Own Ticket "Status / TAT" cell (My Complaints -> Own Tickets) -- makes the
// current status badge itself a clickable, authorized status-change
// control. Modeled on InlineStatusPicker.jsx's click-the-pill popover
// interaction (reuses its stylesheet, ./InlineStatusPicker.css, verbatim --
// same isp-* classes, so the two controls look identical), but this is a
// SEPARATE component rather than a modification of InlineStatusPicker
// itself: that component is shared by other roles/scopes (Manager, My
// Tickets) via the generic GET/POST /tasks/:id[/:taskId]/(allowed-
// transitions|transitions/:id) contract, and this control instead calls the
// DEDICATED, owner-scoped endpoints controllers/taskController.js just
// added -- GET/PUT /tasks/:id/own-status-transitions[/:transitionId] --
// which authorize on ownership alone (see that file's header comments for
// why the generic engine's permission model doesn't fit Own Tickets).
// Never a hardcoded status list: `transitions` is exactly whatever that
// endpoint returns for THIS ticket's current status right now.
//
// Completed is special (task spec): selecting a transition whose
// `to_status.category === 'COMPLETED'` never calls the status-transition
// endpoint at all -- it closes this popover and calls `onCompleteRequested`
// so the caller can open the already-built CompleteTicketModal instead,
// preserving completed_at/completed_by/completion_remark capture. Both the
// green "Complete" action button and this dropdown route to that exact
// same modal -- one completion implementation, never two.
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import api from "../../api";
import { StatusPill } from "./StatusChips";
import "./InlineStatusPicker.css";

const titleCase = (v) => String(v || "-").replace(/_/g, " ").replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());

export default function OwnTicketStatusPicker({ row, onChanged, onCompleteRequested, allowDirectComplete = false, allowDirectInProgress = false }) {
  const [open, setOpen] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  // Screen (viewport) coordinates for the portaled popover -- computed from
  // the trigger's own bounding rect at open time. The popover is rendered
  // into document.body (see the portal below), NOT as a normal child of
  // this table cell, because the table this control lives in
  // (AdminUnitPage.css's `.unit-table { overflow: hidden }`, for its
  // rounded corners) clips any ordinary absolutely-positioned child that
  // extends past the table's own box -- this control's popover was getting
  // silently cut off (only its header visible) whenever its row was near
  // the bottom of the table. Portaling to <body> with `position: fixed`
  // escapes that clipping entirely.
  const [coords, setCoords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transitions, setTransitions] = useState([]);
  const [selected, setSelected] = useState(null); // transition awaiting confirmation
  const [remark, setRemark] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const wrapRef = useRef(null);
  // The popover itself now renders through a portal (see below), so it is
  // no longer a DOM descendant of wrapRef -- both refs must be checked to
  // tell a genuine outside click from a click inside the (portaled) popover.
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const isInside = (target) =>
      (wrapRef.current && wrapRef.current.contains(target)) ||
      (popoverRef.current && popoverRef.current.contains(target));
    const onDocMouseDown = (e) => {
      if (!isInside(e.target)) close();
    };
    // A fixed-position popover stays put on screen while the page scrolls,
    // visually detaching from its trigger -- simplest safe fix is to just
    // close it, same as clicking outside would. Scrolling INSIDE the
    // popover itself (its own scrollable option list) must not close it.
    const onScroll = (e) => { if (!isInside(e.target)) close(); };
    const onResize = () => close();
    document.addEventListener("mousedown", onDocMouseDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setOpen(false);
    setSelected(null);
    setRemark("");
    setError("");
  };

  const openPicker = async () => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (rect) {
      const estimatedPopoverHeight = 260;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const flipUp = spaceBelow < estimatedPopoverHeight && spaceAbove > spaceBelow;
      setOpenUp(flipUp);
      setCoords(
        flipUp
          ? { left: rect.left, bottom: window.innerHeight - rect.top + 6 }
          : { left: rect.left, top: rect.bottom + 6 }
      );
    } else {
      setOpenUp(false);
      setCoords(null);
    }
    setOpen(true);
    setSelected(null);
    setError("");
    setLoading(true);
    try {
      const { data } = await api.get(`/tasks/${row.id}/own-status-transitions`);
      const fetched = Array.isArray(data?.data) ? data.data : [];
      const current = String(row.status || "").toLowerCase();
      const terminal = ["completed", "closed", "cancelled"].includes(current);
      const hasCompleted = fetched.some((t) => t.to_status?.category === "COMPLETED");
      const hasInProgress = fetched.some((t) => String(t.to_status?.code || "").toUpperCase() === "IN_PROGRESS");

      if (
        allowDirectInProgress &&
        row.project_id &&
        ["pending", "assigned"].includes(current) &&
        !hasInProgress
      ) {
        fetched.unshift({
          transition_id: "direct-in-progress-" + row.id,
          code: "DIRECT_IN_PROGRESS",
          name: "Start Work",
          requires_remark: false,
          to_status: { id: null, code: "IN_PROGRESS", name: "In Progress", category: "IN_PROGRESS" },
        });
      }

      // Project/milestone work may use a workflow whose Pending state does
      // not expose a direct -> Completed edge (e.g. only Assign/Cancel).
      // My Tasks still needs an explicit "Complete" action for the actual
      // assignee. This synthetic option never writes status itself: the
      // existing completion modal/PUT /tasks/:id/complete endpoint remains
      // the single path that captures completion date/time + work done.
      if (allowDirectComplete && row.project_id && !terminal && !hasCompleted) {
        fetched.push({
          transition_id: `direct-complete-${row.id}`,
          code: "COMPLETE",
          name: "Complete",
          requires_remark: false,
          to_status: { id: null, code: "COMPLETED", name: "Completed", category: "COMPLETED" },
        });
      }

      setTransitions(fetched);
    } catch (err) {
      setTransitions([]);
      setError(err?.response?.data?.error || "Unable to load status options.");
    } finally {
      setLoading(false);
    }
  };

  const pickTransition = (t) => {
    // Completed never goes through this endpoint -- hand off to the
    // caller's CompleteTicketModal instead (task spec: "must never be able
    // to bypass completion details by selecting Completed from the
    // dropdown").
    if (t.to_status?.category === "COMPLETED") {
      close();
      onCompleteRequested?.(row);
      return;
    }
    setSelected(t);
    setRemark("");
    setError("");
  };

  const confirmChange = async () => {
    if (selected.requires_remark && !remark.trim()) {
      setError("A reason is required for this status change.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      if (selected.code === "DIRECT_IN_PROGRESS") {
        await api.put("/tasks/" + row.id + "/own-status-direct", {
          status_code: "IN_PROGRESS",
          remark: remark.trim() || undefined,
        });
      } else {
        await api.put("/tasks/" + row.id + "/own-status-transitions/" + selected.transition_id, {
          remark: remark.trim() || undefined,
        });
      }
      close();
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update status.");
      setSubmitting(false);
    }
  };

  return (
    <div className="isp-wrap" ref={wrapRef}>
      <button
        type="button"
        className="isp-trigger"
        onClick={() => (open ? close() : openPicker())}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Change status"
        title="Change status"
      >
        <StatusPill status={row.status} />
        <span className={`isp-chevron${open ? " open" : ""}`}>⌄</span>
      </button>

      {open && coords && createPortal(
        <div
          ref={popoverRef}
          className={`isp-popover${openUp ? " isp-popover-up" : ""}`}
          style={{ position: "fixed", left: coords.left, top: coords.top, bottom: coords.bottom }}
        >
          {selected ? (
            <div className="isp-remark-box">
              <div className="isp-popover-head">
                <button type="button" className="isp-back-btn" disabled={submitting} onClick={() => { setSelected(null); setError(""); }} aria-label="Back">‹</button>
                <span>Change Ticket Status</span>
              </div>
              <div style={{ padding: "0 8px", fontSize: 12.5 }}>
                <div><strong>Ticket:</strong> #{row.id} — {row.title}</div>
                <div><strong>Current Status:</strong> {row.status_name || titleCase(row.status)}</div>
                <div><strong>New Status:</strong> {selected.to_status?.name}</div>
              </div>
              <textarea
                autoFocus
                rows={3}
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder={selected.requires_remark ? "Reason *" : "Remark (optional)"}
              />
              {error && <div className="isp-error">{error}</div>}
              <div className="isp-remark-actions">
                <button type="button" className="isp-btn-ghost" disabled={submitting} onClick={() => { setSelected(null); setError(""); }}>
                  Cancel
                </button>
                <button type="button" className="isp-btn-primary" disabled={submitting} onClick={confirmChange}>
                  {submitting ? "Updating…" : "Update Status"}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="isp-popover-head"><span>Change Status</span></div>
              {loading ? (
                <div className="isp-state">Loading…</div>
              ) : error ? (
                <div className="isp-error isp-state">{error}</div>
              ) : transitions.length ? (
                <ul className="isp-list" role="listbox">
                  {transitions.map((t) => (
                    <li key={t.transition_id}>
                      <button type="button" className="isp-option" disabled={submitting} onClick={() => pickTransition(t)}>
                        <span className="isp-option-name">{t.name}</span>
                        <span className="isp-option-target">
                          <StatusPill status={t.to_status?.code || t.to_status?.name} />
                          {t.requires_remark && <span className="isp-remark-flag" title="Requires a reason">✎</span>}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="isp-state">No status changes available right now.</div>
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
